/**
 * ⚙️ ESCOLHAS DO CLIENTE — o que este app liga (passo nº 2 da personalização).
 *
 * Este arquivo é só VALOR (as chaves true/false que o cliente escolhe). O
 * MECANISMO — os tipos, `isFeatureEnabled`, `FEATURE_ROUTES`, os helpers — mora
 * em `src/lib/features.ts` e importa daqui. A separação (BLUEPRINT §2, Fase 2)
 * existe pra sincronizar o molde sem clobber: sync sobrepõe `src/lib` e
 * preserva `src/cliente`, então as escolhas do cliente nunca são atropeladas.
 *
 * Continue importando de `@/lib/features` no app (ele re-exporta tudo daqui) —
 * não importe deste arquivo direto.
 *
 * Cada cliente liga só o que usa. Há TRÊS níveis de interruptor aqui:
 *
 *  A) MÓDULOS NAVEGÁVEIS (têm aba no menu e rota própria) — desligar:
 *       1. Esconde o item no menu lateral e na barra inferior (app-shell.tsx);
 *       2. Bloqueia as rotas dele no middleware (redirect pra /app/inicio).
 *     São os que aparecem em FEATURE_ROUTES (o mapa mora em lib/features.ts).
 *
 *  B) CAPACIDADES (não têm aba própria — ligam um recurso dentro do núcleo).
 *     Ex.: `iaClinica` liga a gravação/transcrição por IA dentro do
 *     Atendimento e o import de foto por IA dentro de Cirurgias. Não têm
 *     rota pra bloquear; o interruptor é lido no ponto de uso (botão some,
 *     server action recusa, rota de API recusa). Ficam FORA de FEATURE_ROUTES.
 *
 *  C) PEÇAS DENTRO DE UM MÓDULO — cards/recursos individuais de uma aba.
 *     Ex.: `FINANCE_OPTIONS` (mais abaixo) liga/desliga gráfico de 12 meses,
 *     reserva de imposto, parcelamento… sem mexer no módulo inteiro. Gatear
 *     SEMPRE no render E na query (ver a regra de ouro lá embaixo).
 *
 * O código do módulo continua no repositório — desligar é reversível e não
 * quebra nada. As tabelas dele no banco também continuam no schema (tabela
 * vazia não custa nada e evita migrations condicionais).
 *
 * O que NÃO tem interruptor (núcleo, todo cliente usa):
 * Início, Agenda, Pacientes, Atendimento/documentos, Clínica (configurações).
 *
 * ⚠️ O MOLDE NASCE NO MÍNIMO (BLUEPRINT §2, 17/07/2026): TUDO que é opcional
 * vem `false` — inclusive cirurgias, financeiro, comunicação e IA, que antes
 * vinham ligados por herança do app original. Personalizar é LIGAR o que o
 * questionário do cliente pediu (montar), nunca desligar sobra (desmontar).
 * Um app recém-copiado tem só o núcleo: Início, Agenda, Pacientes,
 * Atendimento e Clínica.
 */

export const FEATURES = {
  /** Fila cirúrgica: espera → agendada → realizada, fotos, preços. */
  cirurgias: false,
  /** Lembretes/mensagens por WhatsApp (templates + fila de envios). */
  comunicacao: true,
  /**
   * Painel financeiro: receitas por profissional, despesas da clínica com
   * rateio, fechamento de caixa, gráficos e exportação. Desligado esconde a
   * aba E os números de dinheiro da tela de Início (Receita e Ticket médio);
   * os valores continuam sendo registrados no atendimento, só não são
   * exibidos em relatório. Cliente que não controla dinheiro no app → `false`.
   */
  financeiro: true,
  /**
   * SUS — aparato de repasse mensal (capacidade do Financeiro, não é aba): o
   * botão "Lançar SUS" no painel, o modal das 3 rubricas (ambulatório/cirurgias/
   * sobreaviso) e a action de lançamento. NASCE DESLIGADO: só clínica que atende
   * SUS liga. Desligado, o botão some e a action recusa. Anda junto do
   * `financeiro` (sem ele, não há painel onde o botão viveria).
   *
   * Não confundir com EXPOR "sus" no seletor de cobertura (isso é
   * `src/cliente/coverage.ts`): este interruptor liga o LANÇAMENTO MENSAL cheio,
   * específico do modelo SUS. Uma clínica-SUS liga os dois.
   */
  sus: false,
  /**
   * Unimed — import de extrato (capacidade do Financeiro, não é aba): o botão
   * "Importar Unimed", o modal e o parser do CSV do portal Unimed (Windows-1252).
   * NASCE DESLIGADO: só clínica que fatura Unimed e quer importar o extrato liga.
   * Desligado, o botão some e as actions recusam. Anda junto do `financeiro`.
   *
   * Não confundir com EXPOR as coberturas Unimed no seletor
   * (`src/cliente/coverage.ts`): este interruptor liga a IMPORTAÇÃO do extrato.
   */
  unimed: false,
  /**
   * Vitrine da landing pública (capacidade, não é aba): liga a página completa
   * do site — hero, especialidades, equipe, convênios, FAQ e (com
   * `captacaoPublica`) o formulário de captação. DESLIGADA por padrão: o molde
   * nasce como PORTA DE LOGIN (landing mínima — logo, nome, botão Entrar), do
   * jeito "montar nunca desmontar". Só liga quando o cliente quer um site de
   * verdade — aí a vitrine aparece e as seções em `src/app/_landing/` são
   * personalizadas (NOVO-CLIENTE Passo 5). Com ela off, o formulário de
   * captação não aparece mesmo que `captacaoPublica` esteja on (a vitrine é onde
   * ele mora). Sem rota própria → fica fora de FEATURE_ROUTES; lida em `page.tsx`.
   */
  landingVitrine: false,
  /**
   * Mural de avisos internos (posts com prioridade, leitura).
   *
   * ✅ LIGADO em 19/08/2026 (decisão do Henrique). O questionário nunca
   * perguntou sobre isto — conferido nos três materiais (o .md e os dois
   * formulários HTML), zero menção. Ele optou por incluir: são 3 dentistas +
   * 3 secretárias no mesmo lugar, e recado interno hoje não tem casa no app.
   */
  mural: true,
  /**
   * IA clínica (capacidade, não é aba): gravação de consulta por áudio +
   * transcrição + estruturação de prontuário em SOAP, e import de foto de
   * folha cirúrgica por IA. Desligado = nenhuma chamada a OpenAI/Anthropic,
   * botão de gravar some do Atendimento, import por foto some das Cirurgias.
   * Cliente que pede "sem IA" → deixar `false`.
   */
  iaClinica: false,
  /**
   * Arquivo de exames e documentos do paciente (capacidade, não é aba): vira
   * a aba "Documentos" DENTRO da ficha do paciente. Guarda o que o paciente
   * TROUXE — resultado de exame, radiografia, laudo, receita de outro
   * profissional, termo assinado. Não confundir com os documentos que o app
   * EMITE (receita/atestado/pedido de exame), que ficam em Atendimentos.
   *
   * ⚠️ Exige o bucket R2 da clínica (binding `ARQUIVOS`, pasta `paciente-docs/`) — por
   * isso nasce DESLIGADO (ligar sem o bucket deixa o envio quebrado).
   *
   * Conteúdo clínico: a recepção não vê a aba e a query nem roda pra ela.
   * Não tem rota própria → fica FORA de FEATURE_ROUTES; o gate é no ponto de
   * uso (página do paciente + todas as server actions de documento).
   */
  documentosPaciente: false,
  /**
   * Odontograma (capacidade, não é aba própria): vira a aba "Odontograma" na
   * ficha do paciente. Mapa dos dentes em notação FDI, com cárie/restauração/
   * selante marcados POR FACE e ausente/implante/coroa/canal/prótese/fratura no
   * dente inteiro. Guarda o ESTADO da boca; o que ainda vai ser feito vive no
   * plano de tratamento (itens do orçamento com `porDente`).
   *
   * Só faz sentido em cliente ODONTOLÓGICO. Conteúdo clínico: recepção não vê a
   * aba e a query nem roda pra ela. Sem rota própria → fora de FEATURE_ROUTES.
   *
   * Combina com `QUOTE_OPTIONS.porDente`: com os dois ligados, marcar um item
   * do plano como realizado oferece atualizar o dente (nunca faz sozinho).
   */
  odontograma: true,
  /**
   * Galeria de imagens e radiografias (capacidade, não é aba própria): vira a
   * aba "Imagens" na ficha do paciente. Fotos clínicas e radiografias que a
   * CLÍNICA captura, organizadas por data, com comparação antes/depois. É a
   * generalização da galeria das cirurgias pra ficha do paciente.
   *
   * Não confundir com `documentosPaciente`: aquele guarda o que o paciente
   * TROUXE (PDF de exame); esta guarda imagem FEITA no consultório, exibida
   * como galeria pra ver e comparar.
   *
   * ⚠️ Exige o bucket R2 da clínica (binding `ARQUIVOS`, pasta `paciente-imagens/`) — por
   * isso nasce DESLIGADA (ligar sem o bucket deixa o envio quebrado). E exige o
   * consentimento de imagem do paciente antes do upload (LGPD art. 11).
   *
   * Conteúdo clínico: recepção não vê a aba e a query nem roda pra ela. Sem
   * rota própria → fica FORA de FEATURE_ROUTES; o gate é no ponto de uso (página
   * do paciente + todas as server actions de imagem). Útil pra odonto
   * (radiografias) e pra médico (foto clínica, pós-op de consultório).
   */
  galeriaImagens: true,
  /**
   * Confirmação de consulta por link (capacidade, não é aba): o lembrete de
   * véspera ganha um link único; o paciente toca "Confirmo" ou "Preciso
   * remarcar" SEM login e a resposta cai direto na agenda (a recepção vê o
   * badge, não transcreve mais). A rota pública é `/confirmar/[token]` (fora do
   * /app, liberada no middleware); o gate real é no ponto de uso — a página
   * pública recusa com a capacidade desligada, e o link só é anexado ao
   * lembrete quando ela (e `comunicacao`) estão ligadas.
   *
   * Sem bucket, sem segredo. Combina com `comunicacao` (é lá que o link viaja),
   * mas o badge na agenda funciona sozinho. Precisa do `APP_URL`
   * preenchido pra o link sair absoluto (senão cai no origin do navegador).
   */
  confirmacaoLink: true,
  /**
   * Assistente de WhatsApp (capacidade, não é aba): liga a integração com o
   * Assistente Etimos (produto separado, `etimos-assistente`), que atende os
   * pacientes pelo WhatsApp oficial — dúvidas, agendamento, remarcação,
   * cancelamento, confirmação, lembretes e chamada de retorno. O app expõe a
   * API de serviço `/api/v1/assistente/*` (Bearer `ASSISTENTE_API_TOKEN`,
   * contrato em docs/ASSISTENTE-CONECTOR.md), a seção "Horários de atendimento"
   * em Clínica (grade por profissional + bloqueios, de onde saem os horários
   * livres) e o item "Assistente" no menu (login único no painel do assistente,
   * só admin).
   *
   * NASCE DESLIGADO: exige os secrets `ASSISTENTE_API_TOKEN` e
   * `ASSISTENTE_SSO_SECRET` e as vars `ASSISTENTE_URL`/`ASSISTENTE_TENANT` no
   * Worker; sem eles as rotas respondem 503. Desligado, as rotas respondem 404.
   * Nada clínico sai pelo conector (só agenda, nome e telefone). Com isto
   * ligado, atualize a `/privacidade` e confirme o aceite do operador (ANEXO
   * LGPD). O modo de agendamento (`bookingMode`) fica em `clinica.ts`.
   */
  assistente: true,
  /**
   * Dossiê do dia (aba própria, mas é recurso de IA): pra cada paciente
   * agendado do dia, a IA monta um resumo pré-consulta (última consulta e
   * queixa, pendências, exames trazidos, retorno combinado) — o médico abre a
   * manhã sabendo quem vem. Página `/app/dossie`, admin-only.
   *
   * ⚠️ É IA CLÍNICA: só funciona com `iaClinica` TAMBÉM ligado (use
   * `canUseDossie()`, nunca esta chave crua). Ligar `dossieDia` num app "sem IA"
   * não faz nada — a página redireciona. Gera o texto só sob clique (custo de
   * IA) e guarda no cache `daily_briefings`. Conteúdo clínico: privado por
   * médico, recepção não vê. Exige a migration da tabela (ou `db:push`).
   */
  dossieDia: false,
  /**
   * Multi-profissional pleno (capacidade, não é aba): a clínica passa a tratar
   * VÁRIOS profissionais de verdade — cada um com sua identidade (CRM,
   * especialidade, RQE, assinatura) nos documentos, sua comissão (%) com repasse
   * à clínica no financeiro, e a gestão deles na tela Clínica (admin).
   *
   * A agenda e o acesso do financeiro JÁ isolam por médico (cada um vê só o
   * seu, a recepção vê todos) — isso funciona mesmo com esta capacidade
   * desligada. O que ela liga é o resto: sem ela, os documentos saem com o
   * titular único (`DOCTOR_INFO`) e não há comissão nem tela de profissionais.
   *
   * Exige as colunas novas em `doctors` (migration ou `db:push`). Sem segredo,
   * sem bucket. Clínica de um profissional só deixa `false`.
   */
  multiProfissional: true,
  /** Finanças pessoais do titular (contas, cartões, recorrências). */
  minhasFinancas: false,
  /** Solicitações de agendamento vindas do formulário do site público. */
  /**
   * ⛔ DESLIGADO em 19/08/2026 (decisão do Henrique).
   *
   * Era uma CAIXA DE ENTRADA MORTA: a única coisa que cria
   * `appointment_requests` é o formulário público da landing, e ele depende de
   * `landingVitrine` + `captacaoPublica` — as duas desligadas, porque a clínica
   * já tem site próprio (o da rede). O item ainda se chamava "Solicitações do
   * site" num app sem site. Religar exige ligar a captação junto, senão volta
   * a nascer vazio.
   */
  solicitacoes: true,
  /**
   * Captação pública: liga o FORMULÁRIO na landing (seção #agendamento) pra o
   * visitante pedir uma avaliação. O molde é LGPD-first (a landing NÃO coleta
   * dado por padrão) — por isso nasce DESLIGADA e, quando ligada, o formulário
   * EXIGE aceite (aviso + consentimento registrado com data e versão do texto).
   * O envio cai nas Solicitações; com `funil` ligado, o botão "Enviar ao funil"
   * promove a lead. Precisa de `solicitacoes` ligado pra ter onde ver os
   * envios. Sem bucket, sem segredo — só a migration das colunas de consent.
   */
  captacaoPublica: false,
  /** Estoque de materiais do consultório (quantidade, mínimo, alerta). */
  /**
   * ✅ LIGADO em 19/08/2026 (decisão do Henrique). ⚠️ O questionário NUNCA
   * perguntou sobre estoque — conferido nos três materiais. Ele não vem de
   * resposta do cliente; é escolha do Henrique porque clínica odontológica
   * consome material o tempo todo.
   *
   * ⚠️ Estoque só serve se ALGUÉM der baixa. Sem alimentar, a tela passa a
   * mentir sobre o que tem na gaveta — pior do que não existir. Vale combinar
   * com a recepção quem mantém antes de apresentar.
   *
   * A ordem manual de conjuntos (`SUBGROUP_ORDER`) e as fusões de categoria
   * nascem VAZIAS — são ⚙️ PERSONALIZAR quando eles disserem como organizam.
   */
  estoque: true,
  /** Retornos: fila de pacientes a chamar de volta, organizada por mês. */
  retornos: true,
  /**
   * Funil de captação (CRM leve): kanban de leads — quem chegou pelo
   * Instagram/WhatsApp/indicação e ainda não é paciente — nas 6 etapas
   * novo → em conversa → qualificado → agendado → convertido/perdido.
   * Com próxima ação datada (painel "Para hoje"), motivo da perda e botão
   * "virou paciente" (cria a ficha sem redigitar).
   *
   * Recepção vê e opera tudo (lead é dado administrativo — é ela que trabalha
   * o funil). Lead perdido é purgado após 12 meses pelo cron de limpeza.
   * Peças finas em FUNIL_OPTIONS (ex.: valor estimado). Sem tabela de outros
   * módulos, sem bucket — liga com a migration do funil (ou db:push).
   */
  funil: true,
  /**
   * Radar de gestão: painel de indicadores OPERACIONAIS da clínica (taxa de
   * falta, comparecimento, movimento da agenda, novos pacientes por mês,
   * aprovação de orçamentos e a lista de pacientes que mais faltam). É a
   * contraparte do Financeiro — o Financeiro responde "quanto entrou", o Radar
   * responde "como a clínica opera". Só leitura, nenhuma mutation.
   *
   * Admin-only (é painel de gestão do titular, igual à aba Clínica). Os dados
   * saem de `appointments`/`patients`/`quotes` que já existem — desligar só
   * some com o painel, não muda nada no resto. O card de orçamentos só aparece
   * se `orcamentos` também estiver ligado.
   */
  radar: true,
  /**
   * Aniversários e campanhas: lista os aniversariantes do dia/semana e monta
   * campanhas de reativação (pacientes sem atendimento há N meses), cada um com
   * uma mensagem pronta e o link de WhatsApp — na mesma lógica de "fila que a
   * recepção dispara" do módulo `comunicacao`.
   *
   * NÃO cria tabela nova nem exige migration/bucket: tudo é calculado na hora a
   * partir de `patients`/`attendances`. Por isso pode ser ligado num app já no
   * ar sem rodar nada no banco. Recepção e médico veem (é operação de contato,
   * dado administrativo — não evolução clínica).
   */
  campanhas: true,
  /**
   * Orçamentos: propostas de procedimentos pro paciente — itens, desconto e
   * status (rascunho → enviado → aprovado/recusado → concluído). Construído em
   * cima do CATÁLOGO de procedimentos, que é núcleo (gerido em /app/clinica,
   * admin) e não tem interruptor.
   *
   * Serve medicina e odontologia com o MESMO modelo — clínica odontológica só
   * liga também `QUOTE_OPTIONS.porDente` mais abaixo. Nunca duplicar o módulo
   * por especialidade (ver o bloco ORÇAMENTOS em db/schema.ts).
   *
   * Aprovar um orçamento NÃO lança nada no financeiro: o lançamento é um botão
   * manual, por item ou em bloco.
   */
  orcamentos: true,
} as const;

/* ─────────────────────────────────────────────────────────────
   C) PEÇAS DENTRO DE UM MÓDULO — granularidade fina

   Um cliente pode querer o Financeiro mas sem gráfico de 12 meses, ou sem
   reserva de imposto. Estas opções ligam/desligam CARDS e RECURSOS dentro da
   aba, sem mexer no módulo inteiro.

   ⚠️ REGRA DE OURO ao usar uma destas: gatear nos DOIS lugares.
      1. Não renderizar o card; E
      2. Não RODAR a query que o alimenta.
   Só esconder na tela mantém o round-trip no banco e, pior, manda os números
   no payload pro navegador — foi exatamente o vazamento corrigido na blindagem
   do Financeiro (CLAUDE.md §11, pass 2). Padrão no `page.tsx`:
       showX ? getQueryX(...) : Promise.resolve(emptyX())

   Desligar é sempre reversível: o código e as colunas do banco continuam lá.
   ──────────────────────────────────────────────────────────── */

export const FINANCE_OPTIONS = {
  /** Card "Resultado do mês" (recebido − a fatia do médico nas despesas). */
  resultadoMes: true,
  /**
   * Reserva de imposto: % sugerido sobre o resultado do mês.
   * DEPENDE de `resultadoMes` — o editor mora dentro daquele card.
   * Usa a coluna `doctors.tax_reserve_pct` (fica null se desligado).
   */
  reservaImposto: true,
  /** Gráfico de receita dos últimos 12 meses, por origem. */
  graficoReceita: true,
  /**
   * Card R$/hora (consultório × cirurgia particular).
   * ⚠️ COMPARTILHA a query `getFinanceAnalytics` com `graficoReceita` — a
   * consulta roda se QUALQUER um dos dois estiver ligado. Desligar só um não
   * economiza round-trip; desligar os dois economiza.
   */
  rendaHora: true,
  /** Card de fechamento do mês (só aparece nos 10 primeiros dias). */
  fechamentoMes: true,
  /**
   * Fechamento de CAIXA da clínica (tela `/app/financeiro/fechamento`): no mês,
   * tudo o que entrou (lançamentos pagos) e tudo o que saiu (contas pagas),
   * dia a dia e POR FORMA DE PAGAMENTO, com o saldo. Saídas contadas pela data
   * do pagamento ou pela competência (mês da conta), à escolha na tela.
   *
   * Nasceu do relatório "Fechamento de Caixa" do Sisclin que a 1ª clínica
   * odontológica usava todo mês (01/10/2026). Não confundir com
   * `fechamentoMes` (card do médico nos 10 primeiros dias, só receita).
   *
   * Soma a clínica INTEIRA → só o titular (admin) vê, igual à DMED. Lê
   * `attendances` e `expenses`; depende da coluna `expenses.paid_amount_cents`
   * (migration `2026-10-01-financeiro-categorias-baixa.sql`).
   */
  fechamentoCaixa: true,
  /**
   * Cobrança por CARNÊ no ASAAS (banco que a clínica já usa — resposta da
   * gestora em 02/10/2026): o app gera o carnê de boleto das parcelas
   * pendentes, baixa o PDF pra imprimir, recebe o aviso de pagamento e dá baixa
   * sozinho; baixa feita no app (paciente pagou na clínica) é avisada ao Asaas.
   *
   * Ligada, mas DORMENTE até existir a env var `ASAAS_API_KEY` na Vercel (e
   * `ASAAS_WEBHOOK_TOKEN` pros avisos) — sem ela nada aparece e nada é chamado
   * (`isAsaasConfigured()`). Setup no CLAUDE.md do app, §9u.
   */
  asaas: true,
  /** Previsão do mês (ritmo + piso). */
  previsaoMes: true,
  /** Meta mensal de recebimento. DEPENDE de `previsaoMes` (vive no mesmo card). */
  metaMensal: true,
  /** Pendentes antigos (>30d) + botão de cobrança por WhatsApp. */
  cobranca: true,
  /** Exportação do mês em PDF (impressão) e CSV. */
  exportar: true,
  /** Parcelamento de lançamento (vira N linhas irmãs, 1..N). */
  parcelamento: true,
  /**
   * Caixa separado por ÁREA: filtro "Área" no Financeiro que separa o dinheiro
   * da clínica geral do de uma especialidade que a clínica trata como caixa
   * próprio (ver `CAIXA_AREAS` no fim deste arquivo).
   *
   * Nasce do Sisclin, onde as Contas a Receber têm "Tipo Caixa: Clínica Geral |
   * Orto" — a clínica pensa nos dois bolsos separados e conferia assim.
   *
   * ⚠️ Não há coluna no banco: a área é DERIVADA da categoria do procedimento
   * do lançamento (`procedure_types.category`), então funciona no histórico que
   * já existe e não pede SQL. O preço disso é que lançamento SEM procedimento
   * escolhido cai sempre na área padrão — e que recategorizar um procedimento
   * remexe o passado. Com `CAIXA_AREAS` vazia o filtro não aparece.
   */
  caixaPorArea: true,
  /** Calculadora de taxa de maquininha no cartão de crédito. */
  taxaCartao: true,
  /**
   * Recibo pra Imposto de Renda: gera o recibo timbrado em PDF de um
   * atendimento PAGO (paciente, CPF, valor + valor por extenso, data,
   * procedimento, profissional). É o papel que o paciente pede em janeiro pra
   * deduzir no IR dele. Botão "Recibo" no menu da linha do financeiro, só em
   * lançamento pago com paciente. Não confundir com a NFS-e (aquela é nota
   * fiscal eletrônica na prefeitura; este é recibo simples pra dedução).
   */
  recibo: true,
  /**
   * Cobrança por Pix: botão "Cobrar por Pix" no menu do lançamento gera o QR
   * Code + o "copia e cola" com o valor já preenchido, pra recepção mandar no
   * WhatsApp ou mostrar na tela. É Pix ESTÁTICO, SEM gateway — o app NÃO sabe
   * quando o paciente pagou (o dinheiro cai direto na conta; a baixa segue
   * manual). Não confundir com o meio de pagamento "Pix" do lançamento, que é
   * só a etiqueta de como foi pago.
   *
   * ⚠️ Nasce DESLIGADA: exige a chave em `PIX_CONFIG.key` (clinic-config.ts).
   * Sem chave, o botão nem aparece (`isPixConfigured()`). Sem bucket, sem
   * segredo, sem migration — a chave Pix é pública por natureza.
   */
  pix: true,
  /**
   * Relatório DMED: aba "DMED" do Financeiro (admin) que junta, por paciente e
   * CPF, quanto ELE PAGOU do próprio bolso (particular) no ano-calendário — o
   * número que a clínica declara na DMED da Receita e que o paciente deduz no
   * IR dele. É RELATÓRIO pro contador (tela + CSV), não o arquivo do programa
   * oficial da Receita. Convênio/SUS ficam de fora (quem declara é a operadora).
   *
   * Não exige nada externo (os dados já estão no financeiro) — por isso pode
   * nascer ligada; deixei DESLIGADA só pra não poluir o menu de quem não usa.
   * Sem migration, sem bucket, sem segredo.
   */
  dmed: true,
  /** Anexar nota fiscal/boleto na despesa. Exige o bucket R2 da clínica (pasta `despesas-docs/`). */
  anexosDespesa: true,
  /** Gaveta de detalhes da despesa (clique na linha). */
  gavetaDespesa: true,
  /**
   * Lançar despesa por foto (IA lê o boleto/nota).
   * ⚠️ Exige TAMBÉM `iaClinica`. Use `canUseExpensePhoto()`, não esta chave crua.
   */
  despesaPorFoto: false,
  /**
   * Emissão de NFS-e (nota fiscal de serviço) a partir de um atendimento pago,
   * via gateway Focus NFe — aba "Notas fiscais" do Financeiro.
   *
   * ⚠️ Nasce DESLIGADA (ao contrário das outras opções, que vêm true): exige
   * setup externo por cliente — conta no Focus NFe, token no env
   * (`FOCUS_NFE_TOKEN`), inscrição municipal + certificado (no painel do Focus)
   * e os dados fiscais em `FISCAL_CONFIG` (clinic-config.ts). Ligar sem isso
   * deixa a emissão quebrada. Roda em homologação até o cliente pôr
   * `FOCUS_NFE_AMBIENTE=producao`.
   */
  notaFiscal: false,
} as const;

/* ─────────────────────────────────────────────────────────────
   C) PEÇAS DO MÓDULO FUNIL
   ──────────────────────────────────────────────────────────── */

export const FUNIL_OPTIONS = {
  /**
   * Campo "valor estimado" no lead (R$ do tratamento em jogo). Vale ouro pra
   * clínica de procedimento de alto valor (cirurgia, implante); é burocracia
   * pra consulta avulsa — por isso nasce DESLIGADO (decisão do Henrique,
   * 10/07/2026). Desligado, a coluna existe mas fica null e some da tela.
   */
  valorEstimado: true,
} as const;

/* ─────────────────────────────────────────────────────────────
   C) PEÇAS DA CAPACIDADE ODONTOGRAMA
   ──────────────────────────────────────────────────────────── */

export const ODONTOGRAMA_OPTIONS = {
  /**
   * Estilo do desenho do dente na aba Odontograma.
   *
   *  · `anatomico` — três vistas empilhadas por dente (lateral, oclusal,
   *    lateral), como no odontograma de papel. Vestibular e lingual ganham
   *    um dente inteiro de área de toque, em vez dos trapézios finos.
   *  · `quadrado`  — o desenho esquemático original: um quadrado dividido em
   *    cinco. Mais compacto na vertical.
   *
   * Isto é só o PADRÃO: quem estiver usando a aba pode trocar no seletor
   * "Desenho" e a escolha fica no aparelho dele (localStorage). Mudar aqui
   * muda pra quem ainda não escolheu nada.
   *
   * ⚙️ ESCOLHA DO HENRIQUE (04/08/2026): fica no **quadrado** por enquanto. O
   * anatômico foi construído a pedido dele, mas a arte é uma leitura
   * simplificada do odontograma de papel e **ninguém da clínica viu a tela
   * ainda** — então o app abre no desenho que já existia e o Donovan
   * experimenta o novo pelo seletor, quando quiser. Vira `anatomico` aqui
   * depois que ele aprovar. Mudança ADITIVA de propósito: ninguém é
   * surpreendido no meio do atendimento.
   *
   * ⚠️ Os dois estilos leem o MESMO mapa de faces (`surfaceSlots` em
   * `odontograma/lib/teeth.ts`). Trocar o estilo NÃO pode mudar qual face é
   * qual — se algum dia mudar, é bug, não configuração.
   */
  estiloPadrao: "quadrado" as "anatomico" | "quadrado",

  /**
   * O seletor "Desenho" aparece na aba?
   *
   * ⚙️ ESCOLHA DO HENRIQUE (05/08/2026): **não**. A arte do anatômico foi
   * reprovada por ele (vai redesenhar), e os três commits do odontograma
   * subiram pra produção de carona num deploy de fuso, sem que essa fosse a
   * intenção. Com isto em `false`, a clínica volta a ver exatamente o que via.
   *
   * Desligar o seletor também faz o app IGNORAR a preferência já salva no
   * aparelho — senão quem tivesse clicado em "Dente" antes ficaria preso no
   * desenho reprovado, sem botão nenhum pra voltar.
   *
   * O código do anatômico fica todo aqui de propósito: o que vai ser refeito é
   * só o desenho (`tooth-shapes.ts`); o mapa de faces, a área de toque e os
   * testes continuam valendo. Vira `true` quando a arte nova entrar.
   */
  seletorVisivel: false,
} as const;

/* ─────────────────────────────────────────────────────────────
   C) PEÇAS DO MÓDULO ORÇAMENTOS

   ⚠️ REGRA DE PROJETO (09/07/2026) — leia antes de mexer aqui:

   O molde tem UM modelo de orçamento pra medicina e odontologia. A variação
   entre as duas mora em TRÊS camadas, nesta ordem de preferência:

     1. Estas opções — quando o que muda é COMPORTAMENTO (ex.: pedir o dente).
     2. `CLINIC_TYPE` em clinic-config.ts — quando o que muda é só a PALAVRA
        (CRM/CRO). Vocabulário nunca decide comportamento.
     3. O próprio catálogo de procedimentos — que é DADO do cliente, não código.

   Nunca escreva `if (CLINIC_TYPE === "odonto")` pra mudar lógica. No dia em
   que precisar disso, o certo é criar uma opção nova aqui. Sem essa disciplina
   o `if` se espalha e o molde vira dois apps enfiados num só.
   ──────────────────────────────────────────────────────────── */

export const QUOTE_OPTIONS = {
  /**
   * Item do orçamento ganha o campo "dente" (odontologia). Desligado, a coluna
   * `quote_items.tooth` existe mas fica sempre null — e a coluna some da tela.
   * Quando o odontograma existir, é ele que passa a preencher esse campo.
   */
  porDente: true,
  /**
   * Campo "válido até" no orçamento (prazo da proposta).
   */
  validade: true,
} as const;

/* ────────────────────────────────────────────────────────────
   D. ÁREAS DE CAIXA — o dinheiro separado em bolsos

   Preenche o filtro "Área" do Financeiro (opção `caixaPorArea` acima).
   Cada entrada aqui vira uma escolha no filtro; tudo que não casa com
   nenhuma delas cai na área padrão ("Clínica geral").

   A área é DERIVADA da `category` do procedimento do lançamento — as strings
   em `categorias` precisam bater com as categorias do catálogo
   (Clínica → Procedimentos). Categoria escrita errada não estoura: a área
   simplesmente nunca casa e fica vazia. Rode `npm run check:cliente`, que
   confere isso contra o pacote.

   Lista vazia = sem separação, o filtro não aparece.
   ──────────────────────────────────────────────────────────── */

// Sem `as const` de propósito: com ele o TypeScript trava `categorias` no
// literal exato ("Ortodontia") e comparar com uma categoria vinda do banco
// (string qualquer) vira erro de tipo. O formato é o `CaixaArea` de
// `src/lib/features.ts` — anotado ali, não importado aqui (importar criaria
// ciclo: o lib já importa deste arquivo).
export const CAIXA_AREAS: {
  key: string;
  label: string;
  categorias: string[];
}[] = [
  {
    key: "orto",
    label: "Ortodontia",
    categorias: ["Ortodontia"],
  },
];
